// frontend/src/utils/engagementSections.js
//
// The section allowlist for the anonymous page-engagement tracker
// (utils/engagementBeacon.js) - which page regions are measured, and the DOM
// contract that marks them.
//
// MIRRORED IN THE BACKEND. The ingest endpoint (POST /analytics/page-engagement)
// keeps its own copy of these names and DROPS any `sec` key that is not on it,
// so a name added here alone is measured, sent and silently thrown away. A new
// name goes into BOTH lists in the same change.
//
// DOM contract:
//   data-lf-section="<name>"  on a section's ROOT element (SectionShell derives
//                             it from the section's anchor id, or takes an
//                             explicit `trackSection` prop).
//   data-lf-overlay           boolean attribute, ALSO on an overlay section
//                             (a fixed popup card). Overlay time is counted in
//                             parallel with the section under it and never
//                             counts toward scroll depth.

/** Every measurable section name. Order is irrelevant (the backend ranks). */
export const ENGAGEMENT_SECTIONS = Object.freeze([
  "hero",
  "clients",
  "where",
  "how-it-works",
  "features",
  "opportunity",
  "opportunity-types",
  "pricing",
  "numbers",
  "faq",
  "blog",
  "cta-mid",
  "cta-end",
  "popup",
]);

const SECTION_SET = new Set(ENGAGEMENT_SECTIONS);

/** The attribute that names a measured section. */
export const SECTION_ATTR = "data-lf-section";

/** The boolean attribute that marks a measured section as an overlay. */
export const OVERLAY_ATTR = "data-lf-overlay";

/**
 * True only for a name on the allowlist.
 *
 * @param {unknown} name
 * @returns {boolean}
 */
export function isEngagementSection(name) {
  return typeof name === "string" && SECTION_SET.has(name);
}

/**
 * The `data-lf-section` value a section wrapper should carry, or undefined.
 *
 * An explicit `trackSection` wins over the anchor id (two CTA panels share one
 * component and no id, so the caller names them). Either one is used ONLY when
 * it is on the allowlist - an off-list name would be measured and then dropped
 * server-side, so it is better never to tag it at all.
 *
 * @param {string} [id] the section's anchor id
 * @param {string} [trackSection] an explicit section name
 * @returns {string|undefined}
 */
export function resolveSectionTag(id, trackSection) {
  if (trackSection !== undefined && trackSection !== null) {
    return isEngagementSection(trackSection) ? trackSection : undefined;
  }
  return isEngagementSection(id) ? id : undefined;
}
